import { A365BackendModel as Model } from '../a365.backendmodel';
import { SharedModel as Base } from '../sharedmodel';
import { Metamodel } from '../base/model';
import { AdapterRegistry } from './adapter-registry';
import { SparkJobDefinition, SparkJobProperties, SparkJobDefinitionLanguage } from '../sparkjobdefinition/sparkjobdefinition';
import { NamedAdapter } from './named.adapter';
import { ModelConstants } from '../model-constants';
import { SparkConfiguration } from '../sparkconfiguration/sparkconfiguration';
import { ReferenceAdapter } from './reference.adapter';

const SPARK_DOTNET_CLASS_NAME = 'org.apache.spark.deploy.dotnet.DotnetRunner';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SparkJobDefinition),
    adapts: {
        name: '*',
        properties: {
            description: '*',
            targetBigDataPool: '*',
            targetSparkCompute: '*',
            requiredSparkVersion: '*',
            language: '*',
            jobProperties: '*',
            folder: '*',
            targetSparkConfiguration: '*'
        }
    }
})
export class SparkJobDefinitionAdapter extends NamedAdapter<SparkJobDefinition, Model.SparkJobDefinitionResource> {
    protected _toResource(model: SparkJobDefinition): Model.SparkJobDefinitionResource {
        const resource = super._toResource(model) as Model.SparkJobDefinitionResource;
        const jobProperties = model.jobProperties
            ? {
                name: model.jobProperties.name,
                file: model.jobProperties.file,
                className: model.jobProperties.className,
                conf: model.jobProperties.conf
                    ? { ...model.jobProperties.conf }
                    : undefined,
                args: model.jobProperties.args
                    ? [...model.jobProperties.args]
                    : undefined,
                jars: model.jobProperties.jars
                    ? [...model.jobProperties.jars]
                    : undefined,
                pyFiles: model.jobProperties.pyFiles
                    ? [...model.jobProperties.pyFiles]
                    : undefined,
                files: model.jobProperties.files
                    ? [...model.jobProperties.files]
                    : undefined,
                archives: model.jobProperties.archives
                    ? [...model.jobProperties.archives]
                    : undefined,
                driverMemory: model.jobProperties.driverMemory,
                driverCores: model.jobProperties.driverCores,
                executorMemory: model.jobProperties.executorMemory,
                executorCores: model.jobProperties.executorCores,
                numExecutors: model.jobProperties.numExecutors
            }
            : undefined;

        const properties = this.properties(resource);
        properties.targetBigDataPool = {
            referenceName: model.targetSparkCompute,
            type: 'BigDataPoolReference'
        };

        properties.targetSparkConfiguration = this._getSparkConfigurationReference(model.targetSparkConfiguration);

        properties.requiredSparkVersion = model.requiredSparkVersion;
        properties.language = model.language;
        properties.scanFolder = model.scanFolder;
        properties.jobProperties = jobProperties;

        return resource;
    }

    protected _toModel(resource: Model.SparkJobDefinitionResource, model?: SparkJobDefinition): SparkJobDefinition {
        if (!model) {
            model = SparkJobDefinition.MODEL.create<SparkJobDefinition>();
        }
        super._toModel(resource, model);
        model.resourceType = ModelConstants.sparkJobDefinition.toLowerCase() + 's';

        if (resource.properties) {
            model.targetSparkCompute = resource.properties.targetBigDataPool && resource.properties.targetBigDataPool.referenceName;

            if (resource.properties.targetSparkConfiguration) {
                model.targetSparkConfiguration = ReferenceAdapter.toProxyModel<SparkConfiguration>(
                    Metamodel.modelFor(SparkConfiguration),
                    resource.properties.targetSparkConfiguration, this.context);
            }

            model.requiredSparkVersion = resource.properties.requiredSparkVersion;
            model.scanFolder = resource.properties?.scanFolder || false;

            if (resource.properties.language) {
                model.language = resource.properties.language as SparkJobDefinitionLanguage;
            } else {
                // For legacy payload without language property, infer its language from class name.
                const className = resource.properties.jobProperties && resource.properties.jobProperties.className;
                if (!className) {
                    model.language = 'python';
                } else if (className === SPARK_DOTNET_CLASS_NAME) {
                    model.language = 'csharp';
                } else {
                    model.language = 'scala';
                }
            }

            const jobProperties = resource.properties.jobProperties;
            if (jobProperties) {
                model.jobProperties = new SparkJobProperties();
                model.jobProperties.name = jobProperties.name;
                model.jobProperties.file = jobProperties.file;
                model.jobProperties.className = jobProperties.className;
                model.jobProperties.conf = jobProperties.conf
                    ? { ...jobProperties.conf }
                    : undefined;
                model.jobProperties.args = jobProperties.args
                    ? [...jobProperties.args]
                    : undefined;
                model.jobProperties.jars = jobProperties.jars
                    ? [...jobProperties.jars]
                    : undefined;
                model.jobProperties.pyFiles = jobProperties.pyFiles
                    ? [...jobProperties.pyFiles]
                    : undefined;
                model.jobProperties.files = jobProperties.files
                    ? [...jobProperties.files]
                    : undefined;
                model.jobProperties.archives = jobProperties.archives
                    ? [...jobProperties.archives]
                    : undefined;
                model.jobProperties.driverMemory = jobProperties.driverMemory;
                model.jobProperties.driverCores = jobProperties.driverCores;
                model.jobProperties.executorMemory = jobProperties.executorMemory;
                model.jobProperties.executorCores = jobProperties.executorCores;
                model.jobProperties.numExecutors = jobProperties.numExecutors;
            }
        }

        return model;
    }

    private _getSparkConfigurationReference(named: SparkConfiguration): Base.ArtifactReference {
        if (!named) {
            return undefined;
        }
        return ReferenceAdapter.toResource(named, this.context) as Base.ArtifactReference;
    }
}
